import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, Bug, RefreshCw, Wifi } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  StatusBadge,
  type ColumnDef,
} from '@/components/ui'

// ─── Types ──────────────────────────────────────────────────────────────────

interface UnifiSite {
  id: string
  name: string
  desc?: string
}

interface UnifiDevice {
  mac: string
  name?: string
  model?: string
  ip?: string
  type: 'uap' | 'usw' | 'ugw' | 'udm'
  status: 'online' | 'offline'
  uptime?: number
  version?: string
  adopted: boolean
  updateAvailable?: boolean
  num_sta?: number
}

interface UnifiAlert {
  _id: string
  key: string
  msg: string
  datetime: string
  subsystem: string
  severity: 'critical' | 'warning' | 'info'
  device_mac?: string
}

function apiErrMsg(err: unknown): string {
  const e = err as { response?: { data?: { message?: string } }; message?: string }
  return e?.response?.data?.message ?? e?.message ?? 'Unbekannter Fehler'
}

// ─── Column definitions ──────────────────────────────────────────────────────

const DEVICE_TYPE_LABELS: Record<string, string> = {
  uap: 'Access Point',
  usw: 'Switch',
  ugw: 'Gateway',
  udm: 'Dream Machine',
}

const deviceColumns: ColumnDef<UnifiDevice>[] = [
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => <StatusBadge status={row.original.status} size="sm" />,
    size: 100,
  },
  {
    accessorKey: 'name',
    header: 'Gerätename',
    cell: ({ row }) => (
      <span className="font-medium">{row.original.name ?? row.original.mac}</span>
    ),
  },
  {
    accessorKey: 'type',
    header: 'Typ',
    cell: ({ row }) => DEVICE_TYPE_LABELS[row.original.type] ?? row.original.type,
  },
  {
    accessorKey: 'model',
    header: 'Modell',
    cell: ({ row }) => row.original.model ?? '—',
  },
  {
    accessorKey: 'ip',
    header: 'IP',
    cell: ({ row }) => row.original.ip ?? '—',
  },
  {
    accessorKey: 'version',
    header: 'Firmware',
    cell: ({ row }) => row.original.version ?? '—',
  },
  {
    accessorKey: 'num_sta',
    header: 'Clients',
    cell: ({ row }) => (row.original.num_sta != null ? String(row.original.num_sta) : '—'),
    size: 80,
  },
]

const SEVERITY_STATUS: Record<string, string> = {
  critical: 'error',
  warning: 'warning',
  info: 'info',
}

const SEVERITY_LABELS: Record<string, string> = {
  critical: 'Kritisch',
  warning: 'Warnung',
  info: 'Info',
}

const alertColumns: ColumnDef<UnifiAlert>[] = [
  {
    accessorKey: 'severity',
    header: 'Schwere',
    cell: ({ row }) => {
      const s = row.original.severity
      return (
        <StatusBadge
          status={SEVERITY_STATUS[s] ?? 'info'}
          size="sm"
          label={SEVERITY_LABELS[s] ?? s}
        />
      )
    },
    size: 110,
  },
  {
    accessorKey: 'msg',
    header: 'Meldung',
  },
  {
    accessorKey: 'subsystem',
    header: 'Subsystem',
    cell: ({ row }) => row.original.subsystem || '—',
    size: 110,
  },
  {
    accessorKey: 'datetime',
    header: 'Zeitpunkt',
    cell: ({ row }) => {
      const d = new Date(row.original.datetime)
      return isNaN(d.getTime()) ? row.original.datetime : d.toLocaleString('de-DE')
    },
    size: 160,
  },
]

// ─── Sub-components ───────────────────────────────────────────────────────────

// ─── Debug Panel ─────────────────────────────────────────────────────────────

function DebugPanel() {
  const [open, setOpen] = useState(false)

  const {
    data: debugData,
    isFetching,
    error: debugError,
    refetch,
  } = useQuery({
    queryKey: ['unifi', 'debug'],
    queryFn: () => apiClient.get('/api/unifi/debug').then((r) => r.data as Record<string, unknown>),
    enabled: false,
  })

  function handleOpen() {
    setOpen(true)
    void refetch()
  }

  if (!open) {
    return (
      <Button variant="ghost" size="sm" onClick={handleOpen}>
        <Bug className="h-4 w-4 mr-1" />
        Diagnose
      </Button>
    )
  }

  return (
    <Card>
      <Card.Header className="flex items-center justify-between">
        <span className="font-medium flex items-center gap-2">
          <Bug className="h-4 w-4" />
          UniFi Diagnose
        </span>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={() => void refetch()} disabled={isFetching}>
            <RefreshCw className={`h-4 w-4 mr-1 ${isFetching ? 'animate-spin' : ''}`} />
            Neu laden
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
            Schließen
          </Button>
        </div>
      </Card.Header>
      <Card.Body>
        {isFetching && <LoadingState label="Roh-Daten werden abgerufen …" />}
        {debugError && (
          <ErrorState
            title="Debug-Abfrage fehlgeschlagen"
            description={apiErrMsg(debugError)}
            details={String(debugError)}
          />
        )}
        {debugData && (
          <pre className="text-xs overflow-auto max-h-96 bg-gray-50 p-3 rounded border">
            {JSON.stringify(debugData, null, 2)}
          </pre>
        )}
      </Card.Body>
    </Card>
  )
}

function SitePicker({
  sites,
  selected,
  onSelect,
}: {
  sites: UnifiSite[]
  selected: string
  onSelect: (id: string) => void
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {sites.map((site) => (
        <Button
          key={site.id}
          variant={selected === site.id ? 'primary' : 'secondary'}
          size="sm"
          onClick={() => onSelect(site.id)}
        >
          {site.name || site.id}
        </Button>
      ))}
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function UnifiPage() {
  const [selectedSiteId, setSelectedSiteId] = useState<string>('')

  const {
    data: sites,
    isLoading: sitesLoading,
    error: sitesError,
    refetch: refetchSites,
  } = useQuery<UnifiSite[]>({
    queryKey: ['unifi', 'sites'],
    queryFn: () => apiClient.get('/api/unifi/sites').then((r) => r.data as UnifiSite[]),
    refetchInterval: 60_000,
  })

  // Auto-select first site on load
  useEffect(() => {
    if (!selectedSiteId && sites && sites.length > 0) {
      setSelectedSiteId(sites[0].id)
    }
  }, [sites, selectedSiteId])

  const activeSiteId = selectedSiteId || (sites?.[0]?.id ?? '')

  const {
    data: devices,
    isLoading: devicesLoading,
    error: devicesError,
    refetch: refetchDevices,
  } = useQuery<UnifiDevice[]>({
    queryKey: ['unifi', 'devices', activeSiteId],
    queryFn: () =>
      apiClient
        .get(`/api/unifi/sites/${activeSiteId}/devices`)
        .then((r) => r.data as UnifiDevice[]),
    enabled: !!activeSiteId,
    refetchInterval: 60_000,
  })

  const {
    data: alerts,
    isLoading: alertsLoading,
    error: alertsError,
    refetch: refetchAlerts,
  } = useQuery<UnifiAlert[]>({
    queryKey: ['unifi', 'alerts', activeSiteId],
    queryFn: () =>
      apiClient
        .get(`/api/unifi/sites/${activeSiteId}/alerts`)
        .then((r) => r.data as UnifiAlert[]),
    enabled: !!activeSiteId,
    refetchInterval: 60_000,
  })

  function handleRefresh() {
    void refetchSites()
    void refetchDevices()
    void refetchAlerts()
  }

  if (sitesLoading) return <LoadingState label="UniFi-Daten werden geladen …" />

  if (sitesError) {
    return (
      <ErrorState
        title="UniFi nicht erreichbar"
        description={apiErrMsg(sitesError)}
        details={String(sitesError)}
        onRetry={handleRefresh}
      />
    )
  }

  const onlineCount = devices?.filter((d) => d.status === 'online').length ?? 0
  const offlineCount = devices?.filter((d) => d.status === 'offline').length ?? 0
  const criticalAlerts = alerts?.filter((a) => a.severity === 'critical').length ?? 0

  return (
    <div className="space-y-4">
      <PageHeader
        title={
          <span className="flex items-center gap-2">
            <Wifi className="h-5 w-5" />
            UniFi
          </span>
        }
        description="Sites, Geräte und Alerts"
        actions={
          <div className="flex gap-2">
            <DebugPanel />
            <Button variant="ghost" size="sm" onClick={handleRefresh}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Aktualisieren
            </Button>
          </div>
        }
      />

      {(!sites || sites.length === 0) && (
        <EmptyState
          title="Keine Sites gefunden"
          description="Bitte UniFi API-Key unter Einstellungen → Globale Adapter konfigurieren."
        />
      )}

      {sites && sites.length > 0 && (
        <>
          <Card>
            <Card.Header>
              <span className="font-medium">Site auswählen</span>
            </Card.Header>
            <Card.Body>
              <SitePicker
                sites={sites}
                selected={activeSiteId}
                onSelect={setSelectedSiteId}
              />
            </Card.Body>
          </Card>

          <div className="grid grid-cols-3 gap-4">
            <Card>
              <Card.Body>
                <div className="text-sm text-gray-500">Geräte online</div>
                <div className="text-2xl font-semibold text-green-600">{onlineCount}</div>
              </Card.Body>
            </Card>
            <Card>
              <Card.Body>
                <div className="text-sm text-gray-500">Geräte offline</div>
                <div className="text-2xl font-semibold text-red-600">{offlineCount}</div>
              </Card.Body>
            </Card>
            <Card>
              <Card.Body>
                <div className="text-sm text-gray-500 flex items-center gap-1">
                  <AlertTriangle className="h-4 w-4 text-yellow-500" />
                  Kritische Alerts
                </div>
                <div className="text-2xl font-semibold text-red-600">{criticalAlerts}</div>
              </Card.Body>
            </Card>
          </div>

          <Card>
            <Card.Header>
              <span className="font-medium">Geräte</span>
            </Card.Header>
            <Card.Body>
              {devicesError ? (
                <ErrorState
                  title="Geräte konnten nicht geladen werden"
                  description={apiErrMsg(devicesError)}
                  details={String(devicesError)}
                  onRetry={() => void refetchDevices()}
                />
              ) : devicesLoading ? (
                <LoadingState label="Geräte werden geladen …" />
              ) : !devices || devices.length === 0 ? (
                <EmptyState
                  title="Keine Geräte"
                  description="Keine Geräte für diese Site gefunden."
                />
              ) : (
                <DataTable<UnifiDevice>
                  data={devices}
                  columns={deviceColumns}
                  pagination={{ pageSize: 25 }}
                />
              )}
            </Card.Body>
          </Card>

          <Card>
            <Card.Header>
              <span className="font-medium">Aktive Alerts</span>
            </Card.Header>
            <Card.Body>
              {alertsError ? (
                <ErrorState
                  title="Alerts konnten nicht geladen werden"
                  description={apiErrMsg(alertsError)}
                  details={String(alertsError)}
                  onRetry={() => void refetchAlerts()}
                />
              ) : alertsLoading ? (
                <LoadingState label="Alerts werden geladen …" />
              ) : !alerts || alerts.length === 0 ? (
                <EmptyState
                  title="Keine aktiven Alerts"
                  description="Keine offenen Alerts für diese Site."
                />
              ) : (
                <DataTable<UnifiAlert>
                  data={alerts}
                  columns={alertColumns}
                  pagination={{ pageSize: 25 }}
                />
              )}
            </Card.Body>
          </Card>
        </>
      )}
    </div>
  )
}
